import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import { Link, NavLink, withRouter } from 'react-router-dom';

import AlertService from '../../../components/SweetAlert';
import ToastUtil from '../../../components/ToastNotification/ToastUtil';
import { config } from '../../../config';
import { DELETE, GET, newFetch, POST } from '../../../helpers/fetch';
import AppContext from '../../../store/App/contexts';

const DeliveryOrder = ({ history }) => {
  const { SERVER_URL } = config;
  const { fetchSiteOptions, site_options } = React.useContext(AppContext);
  const clientID = parseInt(localStorage.getItem('client'), 10);
  const siteID = parseInt(localStorage.getItem('site'), 10);
  const [deliveryOrder, setDeliveryOrder] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [clients, setClients] = useState([]);
  const [sortModel, setSortModel] = useState([
    { field: 'updatedAt', sort: 'desc' }, // Initial sort field and order
  ]);

  const openInNewTab = (url) => {
    let win = window.open(url, '_blank');
    win.focus();
  };

  const handleDownloadClick = async (
    deliveryOrderId,
    signed_id,
    image,
    siteName,
    catalogues,
    name
  ) => {
    try {
      let api_path = `/delivery-order/view-preview/${deliveryOrderId}`;
      let response = await newFetch(POST, api_path, {
        site: siteName,
        name,
        image,
        signed_id: signed_id,
        catalogues: catalogues,
      });

      if (response.success) {
        response.result && openInNewTab(`${SERVER_URL}/${response.result}`);
      } else {
        // Handle error - maybe show a toast notification
        console.error('Failed to download signed DO');
      }
    } catch (error) {
      console.error('Error downloading signed DO:', error);
      // Show error notification to user
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'DO No.',
      flex: 1,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="row">
            <div className="col-md-12">
              <Link
                className="text-primary "
                to={`/delivery-order/edit/${params.id}`}
              >
                <span className="adjust-fs text-muted">{params.row.id}</span>
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      field: 'associate.sites.client.name',
      headerName: 'Client',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <span
            className="adjust-fs text-muted"
            style={{ color: '#212529', fontSize: 15, fontWeight: 400 }}
          >
            {params.row.client.name || '--'}
          </span>
        );
      },
    },
    {
      field: 'site_id',
      headerName: 'Site',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <span
            className="adjust-fs text-muted"
            style={{ color: '#212529', fontSize: 15, fontWeight: 400 }}
          >
            {site_options.find((file, i) => file.id == params.row.site_id)
              .name || '--'}
          </span>
        );
      },
    },
    {
      field: 'signed_do',
      headerName: 'Signed DO(s)',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        // Check if there are signed records
        if (params.row.signed && params.row.signed.length > 0) {
          // Sort signed records by createdAt in descending order
          const signedRecords = [...params.row.signed].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          return (
            <div>
              {signedRecords.map((signedItem, index) => (
                <div
                  key={signedItem.id}
                  style={{
                    marginBottom:
                      index < signedRecords.length - 1 ? '5px' : '0',
                  }}
                >
                  <button
                    className="btn btn-link p-0 border-0 align-baseline text-start"
                    style={{
                      color: '#0d6efd',
                      fontSize: '15px',
                      fontWeight: 400,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() =>
                      handleDownloadClick(
                        params.row.id,
                        signedItem.id,
                        signedItem.signed_filename,
                        params.row.site?.name,
                        params.row.catalogues,
                        signedItem.assoc
                          ? `${signedItem.assoc.first_name} ${signedItem.assoc.last_name}`
                          : signedItem.signed_by
                      )
                    }
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = 'underline')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = 'none')
                    }
                  >
                    {signedItem.assoc
                      ? `${signedItem.assoc.first_name} ${signedItem.assoc.last_name}`
                      : signedItem.signed_by || '--'}
                  </button>
                  <small className="d-block text-muted">
                    {signedItem.created_date}
                  </small>
                </div>
              ))}
            </div>
          );
        }

        return (
          <span
            className="adjust-fs text-muted"
            style={{ color: '#212529', fontSize: 15, fontWeight: 400 }}
          >
            --
          </span>
        );
      },
      cellClassName: 'no-outline',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div
            className={`badge ${
              params.row.status == 'Completed'
                ? 'bg-primary-custom'
                : params.row.status == 'Ongoing'
                ? 'bg-warning'
                : 'bg-dark '
            } adjust-fs adjust-shadow fw-normal`}
            style={{
              fontSize: 14,
              fontWeight: 200,
              padding: '6px 17px',
              color: '#fff',
            }}
          >
            {params.row.status || '--'}
          </div>
        );
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Date Modified',
      flex: 1,
      resizable: false,
      filterable: false,
      renderCell: (params) => {
        const date = new Date(params.row.createdAt);

        if (!params.row.createdAt) return '--';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}`;

        return (
          <span className="adjust-fs text-muted fw-normal">
            {formattedDate}
          </span>
        );
      },
    },
  ];

  const [data, setData] = React.useState([]);

  const fetchDeliveryOrder = async (pagination, field, sort) => {
    setLoading(true);
    let api_path = `/delivery-order/list/download`;
    let response = await newFetch(POST, api_path, {
      field,
      sort,
      page: pagination.page,
      pageSize: pagination.pageSize,
      client_id: clientID,
      site_id: siteID,
    });

    if (response.success) {
      const deliveryOrderData = response.data.list;
      setDeliveryOrder(deliveryOrderData);
      setTotalRows(response.data.total_record);
    } else setDeliveryOrder([]);

    setLoading(false);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handleDelete = async (id) => {
    const isConfirmed = await AlertService.confirm(
      '',
      'Are you sure you want to delete this Delivery Order?'
    );
    if (isConfirmed) {
      let api_path = `/delivery-order/${id}`;
      let response = await newFetch(DELETE, api_path);

      if (response.success) {
        ToastUtil.success(response.message);
        window.location.reload();
      }
    }
  };

  const fetchClients = async () => {
    let api_path = `/client/${clientID}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      setClients(response.data);
    }
  };

  React.useEffect(() => {
    setData(deliveryOrder !== undefined ? deliveryOrder : []);

    deliveryOrder !== undefined && setLoading(false);
  }, [deliveryOrder]);

  useEffect(() => {
    fetchSiteOptions();
  }, []);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    fetchDeliveryOrder(paginationModel, field, sort);
    fetchClients();
  }, [sortModel, paginationModel, clientID, siteID]);

  return (
    <>
      <div
        className="container-fluid"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingBottom: '6rem',
        }}
      >
        <div className="border-bottom pb-2 py-2 d-flex align-items-center">
          <h6 className="flex-grow-1 pt-4" style={{ color: '#5156BE' }}>
            Delivery Order
          </h6>
          {clients.enable_delivery_order && (
            <NavLink
              to="/delivery-order/add"
              className="text-decoration-none"
              style={{ color: 'inherit' }}
            >
              <button
                className="btn btn-outline-brand ms-3 mt-2 adjust-fs adjust-shadow"
                style={{ width: '200px' }}
              >
                Add New
              </button>
            </NavLink>
          )}
        </div>
        {clients.enable_delivery_order ? (
          <>
            <div className="d-flex flex-row align-items-center justify-content-between py-3">
              <h6 className="flex-grow-1">Delivery Order ({totalRows})</h6>
            </div>
            <div className="table-responsive rounded shadow bg-white">
              <Box sx={{ width: '100%' }}>
                {data && (
                  <DataGrid
                    columns={columns}
                    rows={data || []}
                    rowCount={totalRows || 1}
                    loading={loading}
                    pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={setPaginationModel}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    style={{ minHeight: 100 }}
                    localeText={{ noRowsLabel: '' }}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    getRowHeight={() => 'auto'}
                    sx={{
                      [`& .${gridClasses.cell}`]: {
                        py: 3,
                      },
                      [`& .${gridClasses.columnHeader}`]: {
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#6c757d!important',
                      },
                      [`& .${gridClasses.row}`]: {
                        fontSize: '14px',
                        color: '#5156BE!important',
                        fontWeight: 700,
                      },
                    }}
                    disableSelectionOnClick
                    isRowSelectable={() => false}
                  />
                )}
              </Box>
            </div>
          </>
        ) : (
          <div className="text-muted pt-4">
            The client does not have access to this module.
          </div>
        )}
      </div>
    </>
  );
};

export default withRouter(DeliveryOrder);
